import React, { useEffect, useRef, useState } from 'react';
import Widget from '../../widgets';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  PointElement,
  LineElement,
  BarElement,
  Filler,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  Plugin,
} from 'chart.js';
import { Radar, Bar } from 'react-chartjs-2';
import { useCurrentTheme } from '../../hooks/useCurrentTheme';
import {
  createCenterCirclePlugins,
  formatTopicRadarLabels,
  topicRadarOptions,
} from '../../utils/topicRadar';
import { fetchTopicTreeStats } from '../../services/stats';
import { formatTopicStats } from '../../utils/formatTopicStats';
import { TopicRadarData, WIDGET_KEYS } from '../../types/Widgets';
import { TOPIC_RADAR_FAKE_DATA } from '../../utils/fakeData';
import { examsSelectors } from '../../slices/exams/examsSlice';
import { useSelector } from 'react-redux';

ChartJS.register(
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  PointElement,
  LineElement,
  BarElement,
  Filler,
  Title,
  Tooltip,
  Legend
);

declare module 'chart.js' {
  interface Scale {
    xCenter?: number;
    yCenter?: number;
    drawingArea: number;
  }
}

interface TopicRadarProps {
  isDemo?: boolean;
  showDelete?: boolean;
}

const TopicRadar: React.FC<TopicRadarProps> = ({
  isDemo = false,
  showDelete = false,
}) => {
  const primaryExam = useSelector(examsSelectors.selectPrimaryExam);
  const [topicPerformance, setTopicPerformance] = useState<
    TopicRadarData | null | undefined
  >(null);
  const radarChartRef = useRef<ChartJS<'radar'> | null>(null);
  const barChartRef = useRef<ChartJS<'bar'> | null>(null);
  const { isDarkMode } = useCurrentTheme();

  // Update plugins when theme changes
  useEffect(() => {
    // Unregister existing plugins
    const plugins = createCenterCirclePlugins(isDarkMode);
    Object.values(plugins).forEach((plugin) => {
      ChartJS.unregister(plugin as Plugin);
      ChartJS.register(plugin);
    });

    // Force chart update
    if (radarChartRef.current) {
      radarChartRef.current.update();
    }
  }, [isDarkMode]);

  useEffect(() => {
    const handleResize = () => {
      if (radarChartRef.current) {
        radarChartRef.current.resize();
      }
      if (barChartRef.current) {
        barChartRef.current.resize();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (isDemo) {
      setTopicPerformance(TOPIC_RADAR_FAKE_DATA);
    } else {
      getTopicStats();
    }
  }, [isDemo, primaryExam?.id]);

  const getTopicStats = async () => {
    const res = await fetchTopicTreeStats();
    if (res) {
      const formattedStats = formatTopicStats(res);
      setTopicPerformance(formattedStats);
    }
  };

  const reducedTopicPercentage = topicPerformance
    ? Object.keys(topicPerformance).reduce(
        (acc, key) => {
          acc[key] = topicPerformance[key].percentage;
          return acc;
        },
        {} as { [key: string]: number }
      )
    : {};

  const filteredTopicLabels = Object.keys(reducedTopicPercentage).map((label) =>
    formatTopicRadarLabels(label)
  );
  const filteredTopicData = Object.values(reducedTopicPercentage);

  const data = {
    labels: filteredTopicLabels,
    datasets: [
      {
        data: filteredTopicData,
        fill: true,
        backgroundColor: isDarkMode
          ? 'rgba(255, 255, 255, 0.2)'
          : 'rgba(99, 133, 254, 0.1)',
        borderColor: isDarkMode ? '#FEFEFE' : '#5a68af',
        pointBackgroundColor: 'transparent',
        pointBorderColor: 'transparent',
        pointHoverBackgroundColor: 'transparent',
        pointHoverBorderColor: 'transparent',
        borderRadius: 8,
        barPercentage: filteredTopicLabels.length === 1 ? 0.5 : 0.8,
      },
    ],
  };

  const barChartOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        bodyFont: {
          family: 'var(--font-family-base)',
          size: 12,
        },
        padding: 12,
        cornerRadius: 6,
        displayColors: false,
        callbacks: {
          label: (context) => {
            const value = context.raw as number;
            return `Performance: ${value.toFixed(0)}%`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        grid: {
          display: false,
        },
        ticks: {
          color: isDarkMode ? '#FEFEFE' : '#111215',
          font: {
            family: 'var(--font-family-base)',
          },
          callback: function (value) {
            return `${value}%`;
          },
        },
        border: {
          color: 'rgba(255, 255, 255, 0.3)',
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: isDarkMode ? '#FEFEFE' : '#111215',
          font: {
            family: 'var(--font-family-base)',
          },
        },
        border: {
          color: 'rgba(255, 255, 255, 0.3)',
        },
        offset: filteredTopicLabels.length === 1,
      },
    },
  };

  return (
    <Widget
      widgetKey={WIDGET_KEYS.TOPIC_RADAR}
      name={'topic-radar'}
      title="Topic Performance"
      subtitle={'Topic performance across all exams'}
      showDelete={showDelete}
    >
      {filteredTopicLabels.length > 0 && (
        <>
          {filteredTopicLabels.length <= 2 ? (
            <Bar ref={barChartRef} data={data} options={barChartOptions} />
          ) : (
            <Radar
              ref={radarChartRef}
              data={data}
              options={topicRadarOptions(isDarkMode)}
            />
          )}
        </>
      )}
    </Widget>
  );
};

export default TopicRadar;
